import { z } from 'zod';

import { ReviewWorkflowInsightSchema } from './review-insights';

// ---------------------------------------------------------------------------
// "Reviews" — the team code-review network for a workspace: who reviews whose
// pull requests, across the repos the signed-in user's classic GitHub PAT can
// see. Served by GET /api/account/reviews and the MCP `get_review_network`
// tool, cached per-viewer in Convex (`review_snapshots`). Dates are ISO strings.
//
// Edge weight is "distinct PRs reviewed" (a reviewer counted once per PR — the
// same dedupe `computeReviewReciprocity` applies), NOT raw review submissions,
// so the network reads as review *relationships*, not event counts.
// ---------------------------------------------------------------------------

/** Per-edge tally of the reviewer's terminal review state across the PRs. */
export const ReviewStateTallySchema = z.object({
  approved: z.number().int().nonnegative(),
  changesRequested: z.number().int().nonnegative(),
  commented: z.number().int().nonnegative(),
  dismissed: z.number().int().nonnegative(),
});
export type ReviewStateTally = z.infer<typeof ReviewStateTallySchema>;

/**
 * A directed edge: `reviewer` reviewed `author`'s PRs `count` times (distinct
 * PRs). `states` breaks the count down by the reviewer's terminal state on each
 * PR; `lastReviewedAt` is the most recent review on the edge. Logins are bare
 * strings (not `GithubLoginSchema`) because bot reviewers carry a `[bot]` suffix.
 */
export const ReviewEdgeSchema = z.object({
  reviewer: z.string().min(1),
  author: z.string().min(1),
  count: z.number().int().positive(),
  states: ReviewStateTallySchema,
  lastReviewedAt: z.iso.datetime(),
});
export type ReviewEdge = z.infer<typeof ReviewEdgeSchema>;

/** Per-person stats — one node in the network. */
export const ReviewPersonStatSchema = z
  .object({
    login: z.string().min(1),
    /** Distinct PRs this person reviewed (out-edge weight). */
    reviewsGiven: z.number().int().nonnegative(),
    /** Distinct PRs of this person's that others reviewed (in-edge weight). */
    reviewsReceived: z.number().int().nonnegative(),
    /** Distinct authors this person reviewed (out-degree). */
    distinctAuthorsReviewed: z.number().int().nonnegative(),
    /** Distinct reviewers of this person's PRs (in-degree). */
    distinctReviewers: z.number().int().nonnegative(),
    /** People with whom this person has a two-way review relationship. */
    reciprocalPartners: z.number().int().nonnegative(),
    /** Review-cluster id (Louvain community), or null when in no cluster. */
    community: z.number().int().nonnegative().nullable(),
    /** A bot account (login ends `[bot]` or a known bot). */
    isBot: z.boolean(),
    // --- Review requests (the "Requested" dimension; see ReviewRequestEdge) ---
    /** Distinct PRs this person was *individually* requested to review (in-window). */
    requestsReceived: z.number().int().nonnegative(),
    /** Of `requestsReceived`, the PRs this person then *submitted* a review on. */
    requestsCompleted: z.number().int().nonnegative(),
    /**
     * Distinct PRs where a team this person belongs to was requested — the
     * per-member attribution of team asks, kept OUT of the individual
     * responsiveness denominator (`requestsCompleted/requestsReceived`).
     */
    teamRequestsReceived: z.number().int().nonnegative(),
    // --- Per-person timings (hours; null when there's no sample) ---
    /** Median hours from being requested → this person's review (reviewer responsiveness). */
    reviewResponseHours: z.number().nonnegative().nullable(),
    /** Median hours their PRs wait from a request → that requested review arriving (author). */
    requestWaitHours: z.number().nonnegative().nullable(),
    /** Median hours from their PR opening → its first external review (author). */
    firstReviewWaitHours: z.number().nonnegative().nullable(),
  })
  .refine(
    (stat: {
      readonly requestsCompleted: number;
      readonly requestsReceived: number;
    }) => stat.requestsCompleted <= stat.requestsReceived,
    {
      message: 'requestsCompleted cannot exceed requestsReceived',
      path: ['requestsCompleted'],
    },
  );
export type ReviewPersonStat = z.infer<typeof ReviewPersonStatSchema>;

/**
 * A directed *request* edge: `requestedReviewer` was asked to review `author`'s
 * PRs `count` times (distinct PRs, in-window), of which `completed` were then
 * reviewed (a submitted review) by that reviewer — edge-level responsiveness.
 * Separate from `ReviewEdge`: you can be asked without reviewing, or review
 * without being asked. User (individual) requests only; team asks live in
 * `ReviewTeamRequest`.
 */
export const ReviewRequestEdgeSchema = z
  .object({
    requestedReviewer: z.string().min(1),
    author: z.string().min(1),
    count: z.number().int().positive(),
    completed: z.number().int().nonnegative(),
    lastRequestedAt: z.iso.datetime(),
  })
  .refine(
    (edge: { readonly completed: number; readonly count: number }) =>
      edge.completed <= edge.count,
    {
      message: 'completed cannot exceed count',
      path: ['completed'],
    },
  );
export type ReviewRequestEdge = z.infer<typeof ReviewRequestEdgeSchema>;

/**
 * A team-level review-request tally: a GitHub team was asked to review
 * `requestsReceived` distinct PRs, of which `completed` were reviewed by *any
 * known member* (team responsiveness). `knownMembers` is false when the team
 * couldn't be resolved to members (directory unavailable/truncated past it), in
 * which case `completed`/`memberCount` are 0 and the UI shows the count only.
 */
export const ReviewTeamRequestSchema = z
  .object({
    // `org` namespaces the team so same-slug teams in different orgs (e.g.
    // acme/backend vs beta/backend) stay distinct in the cross-org view.
    team: z.object({
      slug: z.string().min(1),
      name: z.string(),
      org: z.string().min(1),
    }),
    requestsReceived: z.number().int().positive(),
    completed: z.number().int().nonnegative(),
    distinctAuthors: z.number().int().nonnegative(),
    memberCount: z.number().int().nonnegative(),
    knownMembers: z.boolean(),
  })
  .refine(
    (request: {
      readonly completed: number;
      readonly requestsReceived: number;
    }) => request.completed <= request.requestsReceived,
    {
      message: 'completed cannot exceed requestsReceived',
      path: ['completed'],
    },
  );
export type ReviewTeamRequest = z.infer<typeof ReviewTeamRequestSchema>;

/** "Who reviews each other" — a pair with a two-way review relationship. */
export const ReviewMutualPairSchema = z.object({
  a: z.string().min(1),
  b: z.string().min(1),
  /** PRs of b's that a reviewed. */
  aToB: z.number().int().nonnegative(),
  /** PRs of a's that b reviewed. */
  bToA: z.number().int().nonnegative(),
});
export type ReviewMutualPair = z.infer<typeof ReviewMutualPairSchema>;

/** An author whose reviews concentrate on a single reviewer (bus-factor risk). */
export const ReviewSoleReviewerRiskSchema = z.object({
  author: z.string().min(1),
  reviewer: z.string().min(1),
  /** Fraction of the author's received reviews that came from `reviewer`. */
  share: z.number().min(0).max(1),
});
export type ReviewSoleReviewerRisk = z.infer<
  typeof ReviewSoleReviewerRiskSchema
>;

/** A ranked person (top reviewers / most-reviewed). */
export const ReviewRankEntrySchema = z.object({
  login: z.string().min(1),
  count: z.number().int().nonnegative(),
});
export type ReviewRankEntry = z.infer<typeof ReviewRankEntrySchema>;

/** Network-level summary stats. */
/**
 * A "key reviewer" — someone pulling weight ACROSS teams: their review volume,
 * the distinct authors they review, and how many other clusters they review into.
 * Ranked by volume × cross-team reach, so a cross-team workhorse surfaces above a
 * higher-volume reviewer who only reviews their own clique.
 */
export const ReviewKeyReviewerSchema = z.object({
  login: z.string().min(1),
  reviewsGiven: z.number().int().nonnegative(),
  distinctAuthorsReviewed: z.number().int().nonnegative(),
  /** Distinct OTHER review clusters this person reviewed into. */
  teamsReached: z.number().int().nonnegative(),
});
export type ReviewKeyReviewer = z.infer<typeof ReviewKeyReviewerSchema>;

/**
 * PRs grouped by their repo's required approving-review count (current default-branch
 * branch-protection rule). `approved` = PRs whose `reviewDecision` is APPROVED (met the
 * requirement). Repos with an unknown requirement are excluded (see partial reasons).
 */
export const ReviewApprovalRequirementSchema = z
  .object({
    required: z.number().int().nonnegative(),
    prs: z.number().int().nonnegative(),
    approved: z.number().int().nonnegative(),
  })
  .refine(
    (entry: { readonly approved: number; readonly prs: number }) =>
      entry.approved <= entry.prs,
    {
      message: 'approved cannot exceed prs',
      path: ['approved'],
    },
  );
export type ReviewApprovalRequirement = z.infer<
  typeof ReviewApprovalRequirementSchema
>;

// ---------------------------------------------------------------------------
// Per-PR detail (repo, size, changed directories) — carried so the client and
// the MCP can re-scope the matrix/graph by repo/path/size and render a PR list.
// `reviews`/`requests` are the exact records the shared edge builders consume,
// so a no-filter recompute reproduces `edges`/`requestEdges` by construction.
// ---------------------------------------------------------------------------

/** One review on a PR (terminal-state + when submitted) — for the client edge re-tally. */
export const ReviewPullReviewSchema = z.object({
  reviewer: z.string().min(1),
  state: z.string(),
  submittedAt: z.iso.datetime().nullable(),
});
export type ReviewPullReview = z.infer<typeof ReviewPullReviewSchema>;

/** One individual review request on a PR — for the client request-edge re-tally. */
export const ReviewPullRequestSchema = z.object({
  login: z.string().min(1),
  requestedAt: z.iso.datetime(),
});
export type ReviewPullRequest = z.infer<typeof ReviewPullRequestSchema>;

/**
 * A single PR with its repo, size, changed directories, and the compact review/
 * request records. `dirs` are distinct file directories (capped — see
 * `dirsTruncated`); a path filter matches when any `dir` equals or starts with
 * the prefix. `author` is a bare login (bots included). All dates ISO.
 */
export const ReviewPullSummarySchema = z.object({
  number: z.number().int().positive(),
  url: z.string().min(1),
  repo: z.string().min(1),
  title: z.string(),
  /** Lowercased PR state (matches the work-items convention + the shared badges). */
  state: z.enum(['open', 'closed', 'merged']),
  /** Draft PR (open + not ready for review). */
  isDraft: z.boolean(),
  /** GitHub's aggregate review decision. */
  reviewDecision: z
    .enum(['APPROVED', 'CHANGES_REQUESTED', 'REVIEW_REQUIRED'])
    .nullable(),
  createdAt: z.iso.datetime(),
  mergedAt: z.iso.datetime().nullable(),
  /** Target branch; retained so a filtered network can reapply protection rules. */
  baseRefName: z.string().optional(),
  /** Resolved required approvals for this PR's repo/default branch. */
  requiredApprovals: z.number().int().nonnegative().nullable().optional(),
  author: z.string().nullable(),
  additions: z.number().int().nonnegative(),
  deletions: z.number().int().nonnegative(),
  changedFiles: z.number().int().nonnegative(),
  dirs: z.array(z.string()),
  reviews: z.array(ReviewPullReviewSchema),
  requests: z.array(ReviewPullRequestSchema),
  /** GitHub paginated the changed files past the cap (path data is a lower bound). */
  filesTruncated: z.boolean().optional(),
  /** The per-PR directory list itself was capped (path data is a lower bound). */
  dirsTruncated: z.boolean().optional(),
});
export type ReviewPullSummary = z.infer<typeof ReviewPullSummarySchema>;

/** A repo with its in-window PR count (for the repo filter chips). */
export const ReviewRepoCountSchema = z.object({
  repo: z.string().min(1),
  prs: z.number().int().nonnegative(),
});
export type ReviewRepoCount = z.infer<typeof ReviewRepoCountSchema>;

/** A directory with its in-window PR count (for the path-filter suggestion chips). */
export const ReviewDirectoryCountSchema = z.object({
  dir: z.string().min(1),
  prs: z.number().int().nonnegative(),
});
export type ReviewDirectoryCount = z.infer<typeof ReviewDirectoryCountSchema>;

/** One UTC day/hour cell in a reviewer's observed review activity heatmap. */
export const ReviewNetworkTimingCellSchema = z.object({
  /** Sunday is 0, matching JavaScript's UTC `getDay()`. */
  dayOfWeek: z.number().int().min(0).max(6),
  hourOfDay: z.number().int().min(0).max(23),
  reviews: z.number().int().positive(),
});
export type ReviewNetworkTimingCell = z.infer<
  typeof ReviewNetworkTimingCellSchema
>;

/**
 * Observed reviewer response and availability profile. Day/hour cells are UTC
 * aggregates and must be rendered consistently as UTC.
 */
export const ReviewNetworkTimingProfileSchema = z.object({
  login: z.string().min(1),
  reviewCount: z.number().int().positive(),
  avgResponseHours: z.number().nonnegative(),
  medianResponseHours: z.number().nonnegative(),
  preferredDays: z.array(z.number().int().min(0).max(6)),
  preferredHours: z.array(z.number().int().min(0).max(23)),
  approvalRate: z.number().min(0).max(1),
  /** Sparse cells only: omitted cells have zero observed reviews. */
  heatmap: z.array(ReviewNetworkTimingCellSchema),
});
export type ReviewNetworkTimingProfile = z.infer<
  typeof ReviewNetworkTimingProfileSchema
>;

export const ReviewNetworkTimingSchema = z.object({
  profiles: z.array(ReviewNetworkTimingProfileSchema),
  teamAvgResponseHours: z.number().nonnegative().nullable(),
  teamMedianResponseHours: z.number().nonnegative().nullable(),
  totalReviewsAnalysed: z.number().int().nonnegative(),
});
export type ReviewNetworkTiming = z.infer<typeof ReviewNetworkTimingSchema>;

export const ReviewNetworkSummarySchema = z.object({
  /** Sum of all edge counts — total directed reviewer→author PR relationships. */
  reviewRelationships: z.number().int().nonnegative(),
  /** PRs considered in the window (reviewed or not). */
  prsConsidered: z.number().int().nonnegative(),
  /** Distinct people in the network (reviewers ∪ authors). */
  participantCount: z.number().int().nonnegative(),
  /** Distinct directed edges. */
  edgeCount: z.number().int().nonnegative(),
  /** Mean pairwise reciprocity (0 = one-way, 1 = perfectly mutual). */
  reciprocityRatio: z.number().min(0).max(1),
  /** Gini of review load across reviewers (0 = even, 1 = concentrated). */
  reviewerConcentration: z.number().min(0).max(1),
  topReviewers: z.array(ReviewRankEntrySchema),
  topReviewed: z.array(ReviewRankEntrySchema),
  /** Cross-team lynchpins: most reviews across the most clusters. */
  keyReviewers: z.array(ReviewKeyReviewerSchema),
  mutualPairs: z.array(ReviewMutualPairSchema),
  /** Review-cluster sizes, largest first. */
  communitySizes: z.array(z.number().int().nonnegative()),
  /** Reviewers carrying >2× the average review load. */
  bottleneckReviewers: z.array(z.string()),
  /** Authors who never reviewed anyone in the window. */
  isolatedAuthors: z.array(z.string()),
  soleReviewerRisks: z.array(ReviewSoleReviewerRiskSchema),
  // --- Review-request aggregates (the "Requested" dimension) ---
  /** Sum of all request-edge counts — total directed requestedReviewer→author asks. */
  requestRelationships: z.number().int().nonnegative(),
  /** Distinct directed request edges. */
  requestEdgeCount: z.number().int().nonnegative(),
  /** Overall responsiveness: total completed / total received over individual requests (0-1). */
  overallResponsiveness: z.number().min(0).max(1),
  /** Most individually-requested people (by distinct PRs asked). */
  topRequested: z.array(ReviewRankEntrySchema),
  /** Individual requests that were never reviewed (received minus completed). */
  unansweredRequests: z.number().int().nonnegative(),
  /** Distinct teams that received at least one request. */
  teamRequestCount: z.number().int().nonnegative(),
  // --- Velocity & quality (state-of-working-set rates + event-windowed flow) ---
  /** Share of considered PRs with ≥1 external (non-author) submitted review (0-1, current state). */
  reviewRate: z.number().min(0).max(1),
  /** Share of considered PRs whose `reviewDecision` is APPROVED (0-1, current state). */
  approvalRate: z.number().min(0).max(1),
  /** PRs grouped by their repo's required approving-review count (default-branch PRs only). */
  approvalsByRequirement: z.array(ReviewApprovalRequirementSchema),
  /** PRs merged within the window. */
  mergedPrs: z.number().int().nonnegative(),
  /** Merged PRs per week over the window (`mergedPrs / (windowDays/7)`). */
  throughputPerWeek: z.number().nonnegative(),
  /** Weekly merged-PR counts, oldest → newest (for a sparkline). */
  throughputByWeek: z.array(z.number().int().nonnegative()),
  /** Median hours PR open → first external review (in-window reviews); null if none. */
  medianTimeToFirstReviewHours: z.number().nonnegative().nullable(),
  /** Median hours PR open → merged (in-window merges); null if none. */
  medianTimeToMergeHours: z.number().nonnegative().nullable(),
  /** Median hours request → the requested reviewer's review; null if none. */
  medianReviewResponseHours: z.number().nonnegative().nullable(),
  // --- PR size & repo/path facets (over the considered PRs) ---
  /** PRs with size data (additions/deletions known). */
  prsSized: z.number().int().nonnegative(),
  /** Total lines added across considered PRs. */
  totalAdditions: z.number().int().nonnegative(),
  /** Total lines removed across considered PRs. */
  totalDeletions: z.number().int().nonnegative(),
  /** Median PR churn (additions + deletions); null when no sized PRs. */
  medianPrChurnLines: z.number().nonnegative().nullable(),
  /** Median PR changed-file count; null when no sized PRs. */
  medianPrChangedFiles: z.number().nonnegative().nullable(),
  /** Repos by in-window PR count, busiest first (for filter chips when pulls are trimmed). */
  repoBreakdown: z.array(ReviewRepoCountSchema),
  /** Top changed directories by PR count (for path-filter suggestion chips). */
  topDirs: z.array(ReviewDirectoryCountSchema),
  windowDays: z.number().int().positive(),
  generatedAt: z.iso.datetime(),
});
export type ReviewNetworkSummary = z.infer<typeof ReviewNetworkSummarySchema>;

/** The computed network: directed edges, per-person nodes, and the summary. */
export const ReviewNetworkSchema = z.object({
  edges: z.array(ReviewEdgeSchema),
  /** Directed request edges (requestedReviewer→author) — the "Requested" dimension. */
  requestEdges: z.array(ReviewRequestEdgeSchema),
  people: z.array(ReviewPersonStatSchema),
  /** Team-level review-request tallies, busiest first. */
  teamRequests: z.array(ReviewTeamRequestSchema),
  /** Per-PR detail (repo, size, changed dirs) — powers the PR list + repo/path/size filters. */
  pulls: z.array(ReviewPullSummarySchema),
  /** Reviewer timing profiles and UTC day/hour heatmaps over these same PRs. */
  timing: ReviewNetworkTimingSchema.optional(),
  summary: ReviewNetworkSummarySchema,
});
export type ReviewNetwork = z.infer<typeof ReviewNetworkSchema>;

/** Why a network is incomplete — surfaced to the UI/MCP instead of a bare flag. */
export const ReviewPartialReasonSchema = z.enum([
  /** GitHub's 1000-result search cap was hit. */
  'search_cap_reached',
  /** Some PR had more reviewers than we fetched. */
  'reviews_truncated',
  /** Some PR had more review-request events than we fetched. */
  'requests_truncated',
  /** The teams directory couldn't be loaded — team requests show counts only. */
  'teams_unavailable',
  /** Team/member lists were paginated — team responsiveness is a lower bound. */
  'teams_truncated',
  /** Branch-protection required-approval counts couldn't be read — approvals-by-requirement partial. */
  'branch_protection_unavailable',
  /** Some PRs' changed-file/directory lists were capped — a path filter may under-match. */
  'files_truncated',
  /** One or more repo/search pages failed to load. */
  'repo_fetch_failed',
  /** The whole fetch failed and an empty network was returned. */
  'fetch_failed',
  /** More orgs were in scope than the cross-org fan-out fetches — some omitted. */
  'org_scope_truncated',
]);
export type ReviewPartialReason = z.infer<typeof ReviewPartialReasonSchema>;

/**
 * Response envelope for `GET /api/account/reviews` — and the exact shape stored
 * (JSON-serialised) in the `review_snapshots` cache, re-validated on read.
 */
export const ReviewNetworkResponseSchema = z.object({
  /** False when the user has not connected a GitHub PAT. */
  connected: z.boolean(),
  /** Empty when the network is complete. */
  partialReasons: z.array(ReviewPartialReasonSchema),
  network: ReviewNetworkSchema,
  /** Additive cloud workflow/friction projection for member org scopes. */
  workflow: ReviewWorkflowInsightSchema.optional(),
});
export type ReviewNetworkResponse = z.infer<typeof ReviewNetworkResponseSchema>;

/** Allowed look-back windows (days). The default is 30. */
export const REVIEW_WINDOW_DAYS = [7, 30, 90, 180] as const;
export const DEFAULT_REVIEW_WINDOW_DAYS = 30;

/**
 * Max orgs merged into one cross-org review network. Bounds the per-org GitHub
 * search fan-out (cost + secondary rate limits); excess orgs are dropped and
 * flagged `org_scope_truncated`. The MCP tool rejects oversized arrays outright.
 */
export const MAX_REVIEW_ORGS = 20;

/** Narrow an arbitrary number to an allowed window, falling back to the default. */
export function coerceReviewWindow(days: number): number {
  return (REVIEW_WINDOW_DAYS as readonly number[]).includes(days)
    ? days
    : DEFAULT_REVIEW_WINDOW_DAYS;
}

/** An empty network — used for the not-connected and load-failure paths. */
export function emptyReviewNetwork(
  windowDays: number,
  generatedAt: string,
): ReviewNetwork {
  return {
    edges: [],
    requestEdges: [],
    people: [],
    teamRequests: [],
    pulls: [],
    timing: {
      profiles: [],
      teamAvgResponseHours: null,
      teamMedianResponseHours: null,
      totalReviewsAnalysed: 0,
    },
    summary: {
      reviewRelationships: 0,
      prsConsidered: 0,
      participantCount: 0,
      edgeCount: 0,
      reciprocityRatio: 0,
      reviewerConcentration: 0,
      topReviewers: [],
      topReviewed: [],
      keyReviewers: [],
      mutualPairs: [],
      communitySizes: [],
      bottleneckReviewers: [],
      isolatedAuthors: [],
      soleReviewerRisks: [],
      requestRelationships: 0,
      requestEdgeCount: 0,
      overallResponsiveness: 0,
      topRequested: [],
      unansweredRequests: 0,
      teamRequestCount: 0,
      reviewRate: 0,
      approvalRate: 0,
      approvalsByRequirement: [],
      mergedPrs: 0,
      throughputPerWeek: 0,
      throughputByWeek: [],
      medianTimeToFirstReviewHours: null,
      medianTimeToMergeHours: null,
      medianReviewResponseHours: null,
      prsSized: 0,
      totalAdditions: 0,
      totalDeletions: 0,
      medianPrChurnLines: null,
      medianPrChangedFiles: null,
      repoBreakdown: [],
      topDirs: [],
      windowDays,
      generatedAt,
    },
  };
}

/** A not-connected response (no PAT). */
export function emptyReviewNetworkResponse(
  windowDays: number,
  generatedAt: string,
): ReviewNetworkResponse {
  return {
    connected: false,
    partialReasons: [],
    network: emptyReviewNetwork(windowDays, generatedAt),
  };
}

/**
 * The snapshot cache-key version. Baked into `reviewScopeKey`, so bumping it
 * changes every key and old rows are simply never matched (a clean cache miss)
 * — no stale-schema row can parse as valid.
 */
export const REVIEW_SCOPE_VERSION = 'v5';

/**
 * Canonical per-viewer cache key:
 * `v5:<sorted,lower-cased,comma-joined gh-orgs>|<windowDays>`. The route (writer)
 * and the client (reader) both derive it the same way so they agree on the key.
 * Orgs are deduped, lower-cased and sorted so the cross-org selection order never
 * mints a new row. The person focus and the repo/path/size filters are client-side
 * lenses, NOT part of the key — selecting them must not mint a new snapshot row. An
 * empty org list (no orgs in scope) yields `v5:|<windowDays>`. Bumped to v5 so rows
 * written before the per-PR title/state/size/dirs and `pulls`/facet fields existed
 * miss cleanly rather than parsing as valid.
 */
export function reviewScopeKey(
  orgs: readonly string[],
  windowDays: number,
): string {
  const joined = [
    ...new Set(
      orgs.map((org) => org.trim().toLowerCase()).filter((org) => org !== ''),
    ),
  ]
    .toSorted((a, b) => a.localeCompare(b))
    .join(',');
  return `${REVIEW_SCOPE_VERSION}:${joined}|${windowDays}`;
}
